import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT, DISCIPLINES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full px-4 py-3 rounded border border-stone-300 bg-white text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-700";
const labelCls = "block text-xs font-bold text-stone-700 uppercase mb-2";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", company: "", phone: "", discipline: DISCIPLINES[0], message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Project details submitted. Steve Lamphear will respond directly.");
      setForm({ name: "", company: "", phone: "", discipline: DISCIPLINES[0], message: "" });
    } catch (err) {
      toast.error(`Submission failed. Please email ${CONTACT.email} directly.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <h2 className="text-xs font-bold uppercase tracking-widest text-amber-800 mb-2">Direct Principal Enquiry</h2>
            <p className="font-display text-3xl sm:text-4xl font-bold text-stone-950 mb-6">Initiate Your Project Scope</p>
            <p className="text-stone-600 leading-relaxed mb-8">
              Contact Steve Lamphear directly to review parcel boundaries, stormwater compliance, or construction modeling needs.
            </p>

            <div className="space-y-4 text-sm text-stone-800">
              <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                <span className="block text-xs font-bold text-stone-500 uppercase">Email Address</span>
                <a href={`mailto:${CONTACT.email}`} className="text-base font-bold text-stone-900 hover:text-amber-800 transition-colors" data-testid="contact-email">{CONTACT.email}</a>
                <span className="block text-xs text-stone-500 mt-0.5">Checked directly by principal engineer</span>
              </div>
              <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                <span className="block text-xs font-bold text-stone-500 uppercase">Office Location</span>
                <span className="text-base font-bold text-stone-900" data-testid="contact-location">{CONTACT.location}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-stone-50 p-8 sm:p-10 rounded-2xl border border-stone-200 shadow-sm">
              <h3 className="text-xl font-bold text-stone-900 mb-6">Central Ohio Project Submission</h3>
              <form className="space-y-4" onSubmit={handleSubmit} data-testid="enquiry-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="enquiry-name" className={labelCls}>Contact Name</label>
                    <input id="enquiry-name" type="text" required placeholder="Your Name" value={form.name} onChange={set("name")} className={inputCls} data-testid="enquiry-name-input" />
                  </div>
                  <div>
                    <label htmlFor="enquiry-company" className={labelCls}>Company / Organization</label>
                    <input id="enquiry-company" type="text" placeholder="Organization" value={form.company} onChange={set("company")} className={inputCls} data-testid="enquiry-company-input" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="enquiry-phone" className={labelCls}>Phone</label>
                    <input id="enquiry-phone" type="tel" required placeholder="(614) 000-0000" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="enquiry-phone-input" />
                  </div>
                  <div>
                    <label htmlFor="enquiry-discipline" className={labelCls}>Discipline</label>
                    <select id="enquiry-discipline" value={form.discipline} onChange={set("discipline")} className={inputCls} data-testid="enquiry-discipline-select">
                      {DISCIPLINES.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="enquiry-message" className={labelCls}>Site Address &amp; Description</label>
                  <textarea id="enquiry-message" rows="4" required placeholder="Central Ohio property location, timeline, and project scope..." value={form.message} onChange={set("message")} className={inputCls} data-testid="enquiry-message-textarea"></textarea>
                </div>

                <div className="pt-2">
                  <button type="submit" disabled={submitting} className="w-full py-4 rounded bg-graphite hover:bg-amber-800 disabled:opacity-60 text-white font-bold text-base transition-colors shadow" data-testid="enquiry-submit-btn">
                    {submitting ? "Submitting..." : "Submit Project Details to Principal"}
                  </button>
                  <p className="text-xs text-stone-500 text-center mt-3">We respect your privacy. Senior-level response on every enquiry.</p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
